import { Box, Select, TextInput } from "@mantine/core";
import { useAtom, useAtomValue } from "jotai";
import { TFunction } from "react-i18next";

import Button from "components/Button";
import Flex from "components/Flex";
import PageLayout from "components/PageLayout";
import Text from "components/Text";

import { numInjuredPeopleAtom } from "atoms/accidentInformation";
import { injuredPersonsAtom } from "atoms/injuredPersons";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useDigitalJourneyPageRouting } from "hooks/useDigitalJourneyPageRouting";
import { useEventTracking } from "hooks/useEventTracking";
import { InjuredRole } from "types/DigitalJourneySession";

export const DJInjuredPeopleDetailsPage = () => {
  const { t } = useClientTranslation();
  const { trackEvent } = useEventTracking();
  const { onNext, continueCTA } = useDigitalJourneyPageRouting();
  const numInjuredPeople = useAtomValue(numInjuredPeopleAtom);
  const [injuredPersons, setInjuredPersons] = useAtom(injuredPersonsAtom);
  const isDisabled = injuredPersons
    .slice(0, numInjuredPeople || 0)
    .some((injuredPerson) => !injuredPerson.firstName || !injuredPerson.lastName || !injuredPerson.role);

  const onContinue = () => {
    trackEvent({ name: "Digital Journey Injured People Details - continue" });
    onNext();
  };

  const isDriverAlreadySelected = injuredPersons.some((person) => person.role === InjuredRole.DRIVER);

  return (
    <PageLayout contentSpacing="space-between">
      <Flex px={24} py={24} gap={16} flex={1} mb={16}>
        {numInjuredPeople &&
          Array.from({ length: numInjuredPeople }).map((_, index) => (
            <Flex gap={16} mt={16} key={"Injured" + index}>
              <Text as="h1" sizeVariant="xl" weight={600}>
                Inserisci le informazioni del ferito #{index + 1}
              </Text>
              <TextInput
                name={`firstName-${index + 1}`}
                value={injuredPersons[index].firstName}
                label={
                  <Text weight={700} sizeVariant="md">
                    Nome*
                  </Text>
                }
                onChange={(e) =>
                  setInjuredPersons((prevValues) => {
                    const people = [...prevValues];
                    const injuredPerson = { ...people[index] };
                    injuredPerson.firstName = e.target.value;
                    people[index] = injuredPerson;

                    return people;
                  })
                }
              />
              <TextInput
                name={`lastName-${index + 1}`}
                value={injuredPersons[index].lastName}
                label={
                  <Text weight={700} sizeVariant="md">
                    Cognome*
                  </Text>
                }
                onChange={(e) =>
                  setInjuredPersons((prevValues) => {
                    const people = [...prevValues];
                    const injuredPerson = { ...people[index] };
                    injuredPerson.lastName = e.target.value;
                    people[index] = injuredPerson;

                    return people;
                  })
                }
              />
              <TextInput
                name={`personalCode-${index + 1}`}
                value={injuredPersons[index].personalCode}
                label={
                  <Text weight={700} sizeVariant="md">
                    Codice fiscale
                  </Text>
                }
                onChange={(e) =>
                  setInjuredPersons((prevValues) => {
                    const people = [...prevValues];
                    const injuredPerson = { ...people[index] };
                    injuredPerson.personalCode = e.target.value;
                    people[index] = injuredPerson;

                    return people;
                  })
                }
              />
              <Box>
                <Text sizeVariant="md" weight={700} mb={4}>
                  Ruolo*
                </Text>
                <Select
                  name={`role-${index + 1}`}
                  placeholder="Seleziona..."
                  data={getPersonRole(t, isDriverAlreadySelected)}
                  value={injuredPersons[index].role}
                  onChange={(value) =>
                    setInjuredPersons((prevValues) => {
                      const people = [...prevValues];
                      const injuredPerson = { ...people[index] };
                      injuredPerson.role = value as InjuredRole;
                      people[index] = injuredPerson;

                      return people;
                    })
                  }
                />
              </Box>
            </Flex>
          ))}
      </Flex>
      <Flex px={24} py={24} gap={16}>
        <Button disabled={isDisabled} onClick={onContinue}>
          {continueCTA}
        </Button>
      </Flex>
    </PageLayout>
  );
};

export const getPersonRole = (t: TFunction, isDriverAlreadySelected = false) => {
  return [
    {
      label: "Conducente del mio veicolo",
      value: InjuredRole.DRIVER,
      disabled: isDriverAlreadySelected,
    },
    {
      label: "Passeggero del mio veicolo",
      value: InjuredRole.PASSENGER,
    },
  ];
};
